import { Dropdown, Checkbox, Tag, Spin } from "antd";
import { ChevronDown } from "lucide-react";
import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { getAppointmentStatusSummary, AppointmentStatusScope } from "../../../api/Appointment";

const STATUS_COLORS: Record<number, string> = {
    1: "blue",
    2: "cyan",
    3: "orange",
    4: "green",
    0: "red",
};

interface StatusOption {
    value: number;
    color: string;
    label: string;
}

interface Props {
    value: number[];
    onChange: (statuses: number[]) => void;
    /** Màn hình đang lọc → lấy đúng danh sách trạng thái của màn hình đó */
    scope?: AppointmentStatusScope;
}

const StatusFilter = ({ value, onChange, scope = "appointment" }: Props) => {
    const { t } = useTranslation();
    const [open, setOpen] = useState(false);
    const [options, setOptions] = useState<StatusOption[]>([]);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        setLoading(true);
        getAppointmentStatusSummary(undefined, scope)
            .then(res => {
                if (res.status && res.data?.length) {
                    setOptions(
                        res.data
                            .filter(item => item.text !== 'Tất cả')
                            .map(item => ({
                                value: Number(item.status),
                                color: STATUS_COLORS[Number(item.status)] || "default",
                                label: item.text || String(item.status),
                            }))
                    );
                }
            })
            .catch(() => {})
            .finally(() => setLoading(false));
    }, [scope]);

    const toggle = (val: number, checked: boolean) => {
        if (checked) {
            onChange([...value, val]);
        } else {
            onChange(value.filter(v => v !== val));
        }
    };

    const content = (
        <div className="p-3 w-[200px] max-h-[220px] overflow-y-auto bg-white dark:bg-[#1f1f1f] rounded-lg shadow-lg space-y-2">
            {loading ? (
                <div className="flex justify-center py-4"><Spin size="small" /></div>
            ) : (
                options.map(opt => (
                    <Checkbox
                        key={opt.value}
                        className="flex w-full"
                        checked={value.includes(opt.value)}
                        onChange={e => toggle(opt.value, e.target.checked)}
                    >
                        <Tag color={opt.color}>{opt.label}</Tag>
                    </Checkbox>
                ))
            )}
        </div>
    );

    const activeCount = value.length;

    return (
        <Dropdown
            dropdownRender={() => content}
            trigger={["click"]}
            open={open}
            onOpenChange={setOpen}
            getPopupContainer={() => document.body}
        >
            <div
                className={`flex items-center gap-1 px-3 py-1 rounded cursor-pointer transition text-000000e0 text-[14px]
                    ${open || activeCount > 0
                        ? "bg-blue-100 !text-[#3e91ab] dark:bg-[#2a2a2a]"
                        : "bg-gray-100 dark:bg-[#1f1f1f]"
                    }`}
            >
                {t("lead.filter_status")}
                {activeCount > 0 && (
                    <span className="ml-1 bg-[#3e91ab] text-white text-[11px] rounded-full px-1.5 leading-tight">
                        {activeCount}
                    </span>
                )}
                <ChevronDown
                    size={16}
                    className={`transition-transform duration-200 ${open ? "rotate-90" : ""}`}
                />
            </div>
        </Dropdown>
    );
};

export default StatusFilter;